import { OnDestroy, Pipe, PipeTransform } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { Subscription } from 'rxjs';
import { QuestionnaireRequestService } from 'src/app/core/api/questionnaire-request.service';
import { IntakeForm, QuestionnaireRequest } from './../../../API';

@Pipe({
  name: 'questionnaireScore',
  pure: false
})
export class QuestionnaireScorePipe implements PipeTransform, OnDestroy {
  private translations: Record<string, string> = {};
  private translationSub: Subscription;
  private lastTransformResult: string = '';

  constructor(
    private questionnaireRequestService: QuestionnaireRequestService,
    private translocoService: TranslocoService
  ) {
    this.translationSub = this.translocoService
      .selectTranslateObject('QUESTIONNAIRE_RESPONSE')
      .subscribe(map => {
        this.translations = map;
        this.lastTransformResult = '';
      });
  }

  transform(
    questionnaire: Partial<QuestionnaireRequest> | Partial<IntakeForm>,
    showDisclaimer: boolean = false,
    disclaimerHtml: string = ''
  ) {
    if (this.lastTransformResult) {
      return this.lastTransformResult;
    }

    if (!this.translations || !this.translations['atRisk']) {
      this.lastTransformResult = 'N/A';
      return this.lastTransformResult;
    }

    if (!(questionnaire && questionnaire.response && questionnaire.response['scores'])) {
      this.lastTransformResult = this.translocoService.translate('N/A');
      return this.lastTransformResult;
    }

    if (showDisclaimer && !disclaimerHtml) {
      disclaimerHtml = `<div class="disclaimer">${this.translations['scoreDisclaimer']}</div>`;
    }

    const scores =
      typeof questionnaire.response['scores'] === 'string'
        ? questionnaire.response['scores']
        : JSON.stringify(questionnaire.response['scores']);

    const scoreJSON = JSON.parse(scores);

    if (
      Object.keys(scoreJSON).length === 0 ||
      scoreJSON['CUSTOM'] === 'N/A' ||
      questionnaire['type'] === 'DERFS' ||
      questionnaire['type'] === 'CDERFS'
    ) {
      this.lastTransformResult = this.translocoService.translate('N/A');
      return this.lastTransformResult;
    }

    const riskKey = this.questionnaireRequestService.isAtRisk(scores) ? 'atRisk' : 'notAtRisk';
    const riskClass = riskKey === 'atRisk' ? 'at-risk' : 'not-at-risk';
    const riskText = this.translations[riskKey];

    const isAtRisk =
      `<span class="${riskClass}"><b>${riskText}</b></span>` +
      (showDisclaimer ? disclaimerHtml : '');

    let res = '';
    Object.keys(scoreJSON).forEach(key => {
      const friendlyName =
        this.questionnaireRequestService.questionnaireTypeToFriendlyNameMap[key] || key;
      res =
        res +
        (res !== '' ? ' | ' : '') +
        `<b>${this.translations[friendlyName] || friendlyName}:</b> ${scoreJSON[key]}`;
      const category = this.questionnaireRequestService.getCategorizedScore(key, scoreJSON[key]);
      if (category) {
        res += ` (${this.translocoService.translate(category)})`;
      }
    });

    this.lastTransformResult = res + '<br>' + isAtRisk;
    return this.lastTransformResult;
  }

  ngOnDestroy(): void {
    this.translationSub.unsubscribe();
  }
}
